<template>
  <div class="chart">
    <Empty
      v-show="isEmpty"
      class="chart-empty"
      tips="当前数据为空"
    />
    <div
      ref="chart"
      style="height: 500px"
    />
  </div>
</template>

<script>
import echarts from 'echarts';

import Empty from '@/components/Empty';

import { getMaxIntFromNumbers } from '@/common/chart';
import { getField } from '@/config/columns/';
import defaultOptions from '@/config/chart/line';
import { isEmpty } from '@/common/util';

export default {
  components: {
    Empty,
  },

  props: {
    dataSource: {
      type: Object,
      required: true,
      default() {
        return {};
      },
    },

    field: {
      type: String,
      required: true,
    },

    type: {
      type: String,
      required: true,
    },
  },

  computed: {
    date() {
      return isEmpty(this.dataSource.date);
    },

    list() {
      return isEmpty(this.dataSource.list);
    },

    labels() {
      return isEmpty(this.dataSource.labels);
    },

    isEmpty() {
      return !(this.date && this.list);
    },
  },

  watch: {
    dataSource() {
      this.render();
    },
  },

  mounted() {
    this.chart = echarts.init(this.$refs['chart']);
    window.addEventListener('resize', this.resize, false);
  },

  destroyed() {
    window.removeEventListener('resize', this.resize, false);
  },

  methods: {
    resize() {
      this.chart.resize();
    },

    render() {
      if (this.isEmpty) return;

      // 指标
      const field = getField(this.field);

      let options;
      if (this.type === 'all') {
        options = this.renderAll(field);
      } else if (this.type === 'top') {
        options = this.renderTop(field);
      }

      const opt = Object.assign(defaultOptions, options);
      opt.dataZoom = [
        {
          type: 'inside',
          start: 0,
          end: 100,
        },
        {
          start: 0,
          end: 100,
          handleIcon:
            'M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
          handleSize: '80%',
          handleStyle: {
            color: '#fff',
            shadowBlur: 3,
            shadowColor: 'rgba(0, 0, 0, 0.6)',
            shadowOffsetX: 2,
            shadowOffsetY: 2,
          },
        },
      ];
      this.chart.setOption(opt, true);
    },

    // 渲染汇总图表
    renderAll(field) {
      const options = {
        xAxis: [],
        yAxis: [],
        series: [],
      };

      if (this.list) {
        options.series.push({
          type: 'line',
          smooth: true, // 启用平滑曲线
          lineStyle: {
            color: '#0096eb',
          },
          itemStyle: {
            color: '#0096eb', // 端点颜色（支持所有CSS颜色格式）
            borderColor: '#000', // 边框颜色（可选）
            borderWidth: 1,     // 边框宽度（可选）
          },
          name: field.name || '未知指标',
          data: this.list[field.key],
        });
      }

      if (this.date) {
        options.xAxis = {
          type: 'category',
          data: this.date,
        };
      }

      if (this.list) {
        options.yAxis = {
          type: 'value',
          min: 0,
          max: getMaxIntFromNumbers(this.list[field.key]),
          splitNumber: 5,
          interval: (getMaxIntFromNumbers(this.list[field.key]) - 0) / 5,
          axisLabel: {
            formatter: '{value}',
          },
        };
      }

      return options;
    },

    // 渲染 TOP10 图表
    renderTop(field) {
      const options = {
        xAxis: [],
        yAxis: [],
        series: [],
      };

      if (this.date) {
        options.xAxis = {
          type: 'category',
          data: this.date,
        };
      }

      // 用于存放全部的数值来计算 y 轴的最大值
      const temp = [];

      if (this.list) {
        this.list.forEach((item, index) => {
          // 当前数组中的最大值
          const max = Math.max.apply(null, item);
          temp.push(max);
          options.series.push({
            type: 'line',
            smooth: true, // 启用平滑曲线
            name: this.labels[index],
            data: item,
          });
        });
      }

      options.yAxis.push({
        type: 'value',
        min: 0,
        max: getMaxIntFromNumbers(temp),
        splitNumber: 5,
        interval: (getMaxIntFromNumbers(temp) - 0) / 5,
        axisLabel: {
          formatter: '{value}',
        },
      });

      return options;
    },
  },
};
</script>

<style lang="less" scoped>
.chart {
  position: relative;

  &-empty {
    width: 100%;
    position: absolute;
    height: 400px;
    z-index: 10;
    background: #fff;
  }
}
</style>